import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useImperativeHandle } from 'react';

import styled, { ThemeProvider } from 'styled-components';

import { useClickOutside } from '../../../ModernScripts/Spotlight/Hooks/useClickOutside';
import { camelToKebab } from '../../../Scripts/Spotlight/Components/CamelToKebab';
import { COLOR, FONT_SIZE, FONT_STACK, RADIUS, SHADOW, SPACING, THEME } from '../../packages/constants';
import { SortDown } from '../../packages/icons';
import TriangleDown from '../../packages/icons/triangle-down';
import { Button, ButtonAsText, ButtonGroup } from '../button';
import { CheckboxInput, CheckboxInputProps } from '../checkbox-input';
import { MultiSelectVariants } from '../multi-select/multi-select';
import { SearchFilter } from '../search-filter/search-filter';

export interface DataItem {
    name: string;
    id: string;
    selected: boolean;
    flagColour?: string;
}

export interface Category {
    categoryName: string;
    options: DataItem[];
}

interface StyledCheckboxProps extends CheckboxInputProps {
    hasMultipleCategories: boolean;
    hasModernVariant?: boolean;
}

export interface MultiSelectInputButtonProps {
    dataId: string;
    onClick: React.MouseEventHandler;
}

type triggerEl = ({ dataId, onClick }: MultiSelectInputButtonProps) => JSX.Element;

export interface MultiSelectInputProps {
    dataSet: Category[];
    dataId: string;
    handleApplyButton: (e?: Category[]) => void;
    handleCancelButton: (e?: DataItem[]) => void;
    disabled?: boolean;
    maxHeight?: string;
    maxWidth?: string;
    hasFilter?: boolean;
    trigger?: triggerEl;
    variant: MultiSelectVariants;
}

interface DropDownValueProps {
    isDropdownOpen: boolean;
    disabled?: boolean;
    hasLegacyStyles: boolean;
}

const DropDownValue = styled.button<DropDownValueProps>`
    border-radius: ${(props) =>
        props.hasLegacyStyles ? 0 : props.isDropdownOpen ? `${RADIUS.DEFAULT} ${RADIUS.DEFAULT} 0 0` : RADIUS.DEFAULT};
    border: 1px solid ${COLOR.MAIN.FORM_FIELD};
    background-color: ${COLOR.PRIMARY.WHITE};
    color: ${COLOR.PRIMARY.BLACK};
    padding: 6px;
    min-width: 120px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: ${FONT_STACK};
    font-size: ${(props) => (props.hasLegacyStyles ? '13px' : FONT_SIZE.SMALL)};
    line-height: ${(props) => (props.hasLegacyStyles ? '18px' : FONT_SIZE.MEDIUM)};
    opacity: ${(props) => (props.disabled ? '0.6' : '1')};
    cursor: pointer;
    position: relative;
`;

interface StyledDropdownProps {
    maxWidth?: string;
    ref: any;
    hasModernVariant?: boolean;
    width: string;
}

const StyledDropdown = styled.div<StyledDropdownProps>`
    background-color: ${COLOR.PRIMARY.WHITE};
    border: 1px solid ${COLOR.DROPDOWN.DROPDOWN_BORDER};
    border-radius: ${RADIUS.DEFAULT};
    box-shadow: ${SHADOW.DEFAULT};
    position: absolute;
    z-index: 100;
    margin-top: ${SPACING.X_SMALL};
    max-width: ${(props) => props.maxWidth ?? 'none'};
    width: ${(props) => props.width};
`;

const StyledCheckbox = styled(CheckboxInput)<StyledCheckboxProps>`
    &&& {
        align-items: flex-start;
        overflow-wrap: anywhere;
    }

    span {
        margin-top: 1px;
        font-size: 13px;
        cursor: pointer;
    }

    input {
        cursor: pointer;
    }
`;

const OptionCheckbox = styled(StyledCheckbox)<StyledCheckboxProps>`
    &&& {
        margin-left: ${(props) => (props.hasMultipleCategories ? SPACING.LARGE : 0)};
        margin-bottom: 0;
    }
`;

const AllCheckbox = styled(StyledCheckbox)<StyledCheckboxProps>`
    &&& {
        margin-top: ${(props) => (props.hasModernVariant ? SPACING.SMALL : SPACING.LARGE)};
        margin-bottom: ${(props) =>
            props.hasModernVariant ? 0 : props.hasMultipleCategories ? SPACING.SMALL : SPACING.LARGE};
    }

    span {
        font-weight: ${(props) => (props.hasModernVariant ? 700 : 400)};
    }
`;

const NotFound = styled.p`
    font-size: ${FONT_SIZE.SMALL};
    color: ${COLOR.MAIN.FORM_FIELD_DARK};
    padding-left: ${SPACING.X_LARGE};
`;

const StyledButtonGroup = styled(ButtonGroup)`
    padding: ${SPACING.MEDIUM};
    background-color: ${COLOR.MAIN.BUTTON_GROUP_BG};
    border-top: 1px solid ${COLOR.MAIN.BUTTON_GROUP_BORDER};
    border-radius: 0 0 ${RADIUS.DEFAULT} ${RADIUS.DEFAULT};
`;

interface CheckboxScrollAreaProps {
    maxHeight?: string;
    hasModernVariant?: boolean;
}

const CheckboxScrollArea = styled.div<CheckboxScrollAreaProps>`
    max-height: ${(props) => props.maxHeight};
    overflow: auto;
    padding: 0 ${(props) => (props.hasModernVariant ? SPACING.SMALL : SPACING.MEDIUM)} ${SPACING.MEDIUM};
`;

const CheckboxWrapper = styled.ul``;

const StyledDownArrow = styled(SortDown)`
    padding: 2px;
    align-self: center;
    justify-self: flex-end;
    pointer-events: none;
`;

interface StyledTriangleDownProps {
    dropdownOpenState?: boolean;
}

const StyledTriangleDown = styled(TriangleDown)<StyledTriangleDownProps>`
    height: 8px;
    width: 8px;
    fill: #888;
    transform: rotate(${(props) => (props.dropdownOpenState ? '180deg' : 0)})
        translateY(${(props) => (props.dropdownOpenState ? '2px' : 0)});
`;

interface StyledListItemProps {
    flagColour?: string;
    hasModernVariant?: boolean;
}

const StyledListItem = styled.li<StyledListItemProps>`
    padding-top: ${SPACING.X_SMALL};
    padding-bottom: ${(props) => (props.hasModernVariant ? SPACING.SMALL : SPACING.X_SMALL)};
    position: relative;

    ${(props) =>
        props.flagColour &&
        `&:before {
        content: '';
        position: absolute;
        top: 0px;
        bottom: 1px;
        left: -12px;
        width: 4px;
        background: ${props.flagColour};
    }`}
`;

interface StyledSearchFilterProps {
    variant: MultiSelectVariants;
}

const StyledSearchFilter = styled(SearchFilter)<StyledSearchFilterProps>`
    margin: ${(props) => (props.variant === 'modern' ? '10px' : SPACING.X_SMALL)};
`;

interface CheckboxProps {
    dataId: string;
    category: Category;
    handleAllCheckboxes: (e?: any) => void;
    handleCheckbox: (id: string, categoryName: string) => void;
    filterValue: string;
    hasMultipleCategories: boolean;
    variant: MultiSelectVariants;
}

const checkCheckboxState = (category: Category) => {
    const parentCheckboxId = `checkbox-all-${camelToKebab(category.categoryName)}`;
    const parentCheckboxElement = document.getElementById(parentCheckboxId);
    const checkedValues: boolean[] = [];
    let count = 0;

    category.options.forEach((item: DataItem) => {
        checkedValues.push(item.selected);
        if (item.selected) {
            count += 1;
        }
    });

    let firstValue = false;
    if (parentCheckboxElement) {
        for (let i = 0; checkedValues.length > i; i += 1) {
            if (i === 0) {
                firstValue = checkedValues[0];
            } else {
                if (firstValue !== checkedValues[i]) {
                    // @ts-ignore
                    parentCheckboxElement.indeterminate = true;
                    break;
                } else if (firstValue && firstValue === checkedValues[i]) {
                    // @ts-ignore
                    parentCheckboxElement.indeterminate = false;
                } else if (!firstValue && firstValue === checkedValues[i]) {
                    // @ts-ignore
                    parentCheckboxElement.indeterminate = false;
                }
            }
        }
    }

    if (parentCheckboxElement) {
        // @ts-ignore
        parentCheckboxElement.checked = count === checkedValues.length;
    }
};

const Checkboxes = ({
    dataId,
    category,
    handleAllCheckboxes,
    handleCheckbox,
    filterValue,
    hasMultipleCategories,
    variant,
}: CheckboxProps) => {
    const filteredList = category.options.filter((item: DataItem) => {
        if (filterValue.startsWith(' ')) {
            return item.name;
        } else {
            return item.name.toLowerCase().includes(filterValue.toLowerCase());
        }
    });

    const hasModernVariant = variant === 'modern';

    useEffect(() => {
        checkCheckboxState(category);
    }, [category]);

    return (
        <>
            <StyledListItem hasModernVariant={hasModernVariant}>
                <AllCheckbox
                    hasMultipleCategories={hasMultipleCategories}
                    dataId={`${dataId}-multi-select__checkbox-all-${camelToKebab(category.categoryName)}`}
                    id={`checkbox-all-${camelToKebab(category.categoryName)}`}
                    label={`${hasModernVariant ? 'Select all' : 'All '} ${
                        hasMultipleCategories ? category.categoryName : ''
                    }`}
                    name={`multiselect-${camelToKebab(category.categoryName)}`}
                    onChange={(e) => handleAllCheckboxes(e)}
                    thinLabel
                    inline
                    hasModernVariant={hasModernVariant}
                />
            </StyledListItem>
            {filteredList.length > 0 ? (
                filteredList
                    .sort((a: DataItem, b: DataItem) => a.name.trim().localeCompare(b.name.trim()))
                    .map((item: DataItem) => (
                        <StyledListItem
                            key={item.id || camelToKebab(item.name)}
                            flagColour={item.flagColour}
                            hasModernVariant={hasModernVariant}
                        >
                            <OptionCheckbox
                                dataId={`${dataId}-multi-select__checkbox-${camelToKebab(item.name)}`}
                                id={item.id || camelToKebab(item.name)}
                                label={item.name}
                                name={`multiselect-${camelToKebab(category.categoryName)}`}
                                onChange={() => handleCheckbox(camelToKebab(item.name), category.categoryName)}
                                checked={item.selected}
                                hasMultipleCategories={hasMultipleCategories}
                                thinLabel
                                inline
                            />
                        </StyledListItem>
                    ))
            ) : (
                <li>
                    <NotFound data-test-id={`${dataId}-multi-select__no-result`}>No results found.</NotFound>
                </li>
            )}
        </>
    );
};

interface DropdownProps {
    data: Category[];
    dataId: string;
    setData: (data: Category[]) => void;
    handleApplyButton: () => void;
    handleCancelButton: () => void;
    hasFilter?: boolean;
    maxHeight?: string;
    maxWidth?: string;
    variant: MultiSelectVariants;
    width: string;
}

const Dropdown: React.FC<React.PropsWithChildren<DropdownProps>> = ({
    data,
    dataId,
    setData,
    handleApplyButton,
    handleCancelButton,
    hasFilter,
    maxHeight,
    maxWidth,
    variant,
    width,
}) => {
    const [filterValue, setFilterValue] = React.useState('');
    const [isDirty, setDirty] = React.useState(false);

    const filterInputRef = useRef(null);
    const dropdownRef = useRef<HTMLElement>();

    useClickOutside(dropdownRef, () => {
        switch (variant) {
            case 'legacy':
            case 'normal':
                handleCancelButton();
                break;
            case 'modern':
                handleApplyButton();
        }
    });

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterValue(e.target.value);
    };

    const handleClearButton = () => {
        setFilterValue('');
        // @ts-ignore
        filterInputRef.current.focus();
    };

    const handleCheckbox = (camelToKebabName: string, categoryName: string) => {
        const dataCopy = [...data];

        dataCopy.forEach((category: Category, categoryIndex: number) => {
            if (category.categoryName === categoryName) {
                dataCopy[categoryIndex].options.forEach((item) => {
                    if (camelToKebab(item.name) === camelToKebabName) {
                        item.selected = !item.selected;
                        setData(dataCopy);
                        setDirty(true);
                    }
                });
                checkCheckboxState({ ...category });
            }
        });
    };

    const handleAllCheckboxes = (e: React.ChangeEvent<HTMLInputElement>, categoryIndex: number) => {
        const dataCopy = [...data];
        dataCopy[categoryIndex].options.forEach((item) => {
            item.selected = e.target.checked;
        });

        setData(dataCopy);
        setDirty(true);
    };

    useEffect(() => {
        function handleKeyListener(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                if (variant === 'modern') {
                    handleApplyButton();
                } // @ts-ignore
                else if (document.activeElement === filterInputRef.current && filterInputRef.current.value.length) {
                    handleClearButton();
                } else {
                    handleCancelButton();
                }
            }
        }

        document.addEventListener('keydown', handleKeyListener);

        return function cleanupKeyListener() {
            document.removeEventListener('keydown', handleKeyListener);
        };
    }, [handleCancelButton, handleApplyButton, variant]);

    return (
        <StyledDropdown
            data-test-id={`${dataId}-multi-select__dropdown`}
            maxWidth={maxWidth}
            ref={dropdownRef}
            hasModernVariant={variant === 'modern'}
            width={variant === 'modern' ? width : ''}
        >
            {hasFilter && (
                <StyledSearchFilter
                    dataId={`${dataId}-multi-select`}
                    ref={filterInputRef}
                    placeholder={'Search...'}
                    handleChange={(e) => handleFilterChange(e)}
                    handleClearButton={handleClearButton}
                    searchTerm={filterValue}
                    assistAccess={false}
                    data-test-id={`${dataId}-multi-select__filter`}
                    hasClearButton={variant !== 'modern'}
                    hasSearchIcon={variant === 'modern'}
                    variant={variant}
                />
            )}
            <CheckboxScrollArea maxHeight={maxHeight} hasModernVariant={variant === 'modern'}>
                {data &&
                    data.map((category: Category, index: number) => (
                        <CheckboxWrapper key={category.categoryName}>
                            <Checkboxes
                                dataId={dataId}
                                category={category}
                                handleAllCheckboxes={(e) => handleAllCheckboxes(e, index)}
                                handleCheckbox={handleCheckbox}
                                filterValue={filterValue}
                                hasMultipleCategories={data?.length > 1}
                                variant={variant}
                            />
                        </CheckboxWrapper>
                    ))}
            </CheckboxScrollArea>
            {variant !== 'modern' && (
                <StyledButtonGroup justify={'space-between'}>
                    <ThemeProvider theme={THEME.REPORTING}>
                        <ButtonAsText
                            dataId={`${dataId}-multi-select__button-cancel`}
                            type={'button'}
                            onClick={() => handleCancelButton()}
                        >
                            Cancel
                        </ButtonAsText>
                    </ThemeProvider>
                    <Button
                        dataId={`${dataId}-multi-select__button-apply`}
                        type={'button'}
                        onClick={() => handleApplyButton()}
                        disabled={!isDirty}
                    >
                        Apply
                    </Button>
                </StyledButtonGroup>
            )}
        </StyledDropdown>
    );
};

const defaultFilterString = 'No filter';

export const MultiSelectInput = React.forwardRef<HTMLDivElement, MultiSelectInputProps>(
    (
        {
            dataSet,
            dataId,
            disabled,
            handleApplyButton,
            handleCancelButton,
            hasFilter = true,
            maxHeight = '240px',
            maxWidth,
            trigger,
            variant,
            ...props
        },
        ref,
    ) => {
        const [data, setData] = useState(dataSet);
        const [dropdownOpenState, setDropDownOpenState] = useState(false);
        const [dropdownValue, setDropdownValue] = useState(defaultFilterString);
        const [containerWidth, setContainerWidth] = useState(0);
        const hasLegacyStyles = variant === 'legacy';

        const containerRef = useRef<HTMLDivElement>(null);
        useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => containerRef.current);

        const handleDropdownToggle = () => {
            setDropDownOpenState(!dropdownOpenState);
        };

        const applyButton = () => {
            handleApplyButton(data);
            setDropDownOpenState(false);
        };

        const cancelButton = useCallback(() => {
            const temp = () => handleCancelButton();
            // @ts-ignore
            setData(temp);
            setDropDownOpenState(false);
        }, [handleCancelButton]);

        const Trigger = (props: MultiSelectInputButtonProps) => {
            if (!trigger) {
                return null;
            }

            return trigger(props);
        };

        useEffect(() => {
            setData(dataSet);
        }, [dataSet]);

        useEffect(() => {
            if (!data) {
                return;
            }

            const items = data.flatMap((category) => category.options);
            const totalItemCount = items.length;
            const selectedItemCount = items.filter((item) => item.selected).length;

            const buttonText =
                selectedItemCount === 0
                    ? defaultFilterString
                    : `${selectedItemCount} of ${totalItemCount} item${totalItemCount === 1 ? '' : 's'}`;

            setDropdownValue(buttonText);
        }, [data]);

        const updateContainerWidth = () => {
            const newWidth = containerRef?.current?.children[0].clientWidth;
            setContainerWidth(newWidth ?? 0);
        };

        useEffect(() => {
            updateContainerWidth();

            window.addEventListener('resize', updateContainerWidth);

            return () => {
                window.removeEventListener('resize', updateContainerWidth);
            };
        }, []);

        return (
            <div ref={containerRef} {...props}>
                {trigger && (
                    <Trigger
                        dataId={`${dataId}-multi-select__button-toggle-dropdown`}
                        onClick={() => handleDropdownToggle()}
                    />
                )}
                {!trigger && (
                    <DropDownValue
                        type={'button'}
                        onClick={handleDropdownToggle}
                        isDropdownOpen={dropdownOpenState}
                        data-test-id={`${dataId}-multi-select__button-toggle-dropdown`}
                        disabled={disabled}
                        hasLegacyStyles={hasLegacyStyles}
                    >
                        {dropdownValue}{' '}
                        {hasLegacyStyles ? (
                            <StyledTriangleDown dropdownOpenState={dropdownOpenState} />
                        ) : (
                            <StyledDownArrow fill={COLOR.MAIN.DARK_GREY} width={'6px'} height={'4px'} />
                        )}
                    </DropDownValue>
                )}
                {dropdownOpenState && (
                    <Dropdown
                        data={data}
                        dataId={dataId}
                        hasFilter={hasFilter}
                        maxHeight={maxHeight}
                        maxWidth={maxWidth}
                        setData={setData}
                        handleCancelButton={cancelButton}
                        handleApplyButton={applyButton}
                        variant={variant}
                        width={containerWidth ? `${containerWidth}px` : ''}
                    />
                )}
            </div>
        );
    },
);
MultiSelectInput.displayName = 'MultiSelectInput';
